import axios from "axios";
import { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Box,
  Typography,
  Fade,
  Zoom,
  CircularProgress,
  LinearProgress,
  Chip,
  Alert,
  Snackbar,
  Grid,
  Paper
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import UserLogOut from "./UserLogOut";
import PublishIcon from "@mui/icons-material/Publish";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import CommentIcon from "@mui/icons-material/Comment";
import FactoryIcon from "@mui/icons-material/Factory";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import "./ShiftDetails.css";
import { useMachine } from "./context/MachineContext";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ShiftDetails = () => {
  const location = useLocation();
  const [username] = useState(
    location.state?.username || sessionStorage.getItem("username") || ""
  );
  const [selectedMachineGroupCode, setSelectedMachineGroupCode] = useState("");
  const [machineNames, setMachineNames] = useState([]);
  const [selectedMachineCode, setSelectedMachineCode] = useState("");
  const [operatorNames, setOperatorNames] = useState([]);
  const [selectedOperators, setSelectedOperators] = useState([]);
  const [helperNames, setHelperNames] = useState([]);
  const [selectedHelpers, setSelectedHelpers] = useState([]);
  const [assistantOperatorNames, setAssistantOperatorNames] = useState([]);
  const [selectedAssistantOperators, setSelectedAssistantOperators] = useState(
    []
  );
  const [machineGroups, setMachineGroups] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState({
    groups: true,
    machines: false,
    operators: false
  });
  const [formVisible, setFormVisible] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info"
  });
  const navigate = useNavigate();
  const { setMachineCode } = useMachine();

  useEffect(() => {
    setFormVisible(true);
  }, []);

  useEffect(() => {
    if (username) {
      sessionStorage.setItem("username", username);
    }
  }, [username]);

  const handleSnackbarClose = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  useEffect(() => {
    const fetchMachineGroups = async () => {
      try {
        setLoading((prev) => ({ ...prev, groups: true }));
        const res = await axios.get(`${API_BASE_URL}/MachineGroups/${username}`);
        setMachineGroups(res.data);
        setSnackbar({
          open: true,
          message: "Machine groups loaded successfully!",
          severity: "success"
        });
      } catch (err) {
        console.log(err);
        setSnackbar({
          open: true,
          message: "Failed to load machine groups",
          severity: "error"
        });
      } finally {
        setLoading((prev) => ({ ...prev, groups: false }));
      }
    };
    fetchMachineGroups();
  }, [username]);

  useEffect(() => {
    const fetchMachineNames = async () => {
      if (selectedMachineGroupCode) {
        try {
          setLoading((prev) => ({ ...prev, machines: true }));
          const res = await axios.get(
            `${API_BASE_URL}/MachineNames?groupCode=${selectedMachineGroupCode}`
          );
          setMachineNames(res.data);
          setSelectedMachineCode("");
          setSelectedOperators([]);
          setSelectedHelpers([]);
          setSelectedAssistantOperators([]);
        } catch (err) {
          console.log(err);
          setSnackbar({
            open: true,
            message: "Failed to load machines",
            severity: "error"
          });
        } finally {
          setLoading((prev) => ({ ...prev, machines: false }));
        }
      }
    };
    fetchMachineNames();
  }, [selectedMachineGroupCode]);

  useEffect(() => {
    const fetchAllStaff = async () => {
      if (selectedMachineGroupCode && selectedMachineCode) {
        try {
          setLoading((prev) => ({ ...prev, operators: true }));
          const [operatorsRes, helpersRes, assistantRes] = await Promise.all([
            axios.get(`${API_BASE_URL}/OperatorNames`, {
              params: {
                groupCode: selectedMachineGroupCode,
                machineCode: selectedMachineCode
              }
            }),
            axios.get(`${API_BASE_URL}/HelperNames`, {
              params: {
                groupCode: selectedMachineGroupCode,
                machineCode: selectedMachineCode
              }
            }),
            axios.get(`${API_BASE_URL}/AssistantOperatorNames`, {
              params: {
                groupCode: selectedMachineGroupCode,
                machineCode: selectedMachineCode
              }
            })
          ]);

          setOperatorNames(operatorsRes.data);
          setHelperNames(helpersRes.data);
          setAssistantOperatorNames(assistantRes.data);
          setSnackbar({
            open: true,
            message: "Staff data loaded successfully!",
            severity: "success"
          });
        } catch (err) {
          console.log(err);
          setSnackbar({
            open: true,
            message: "Failed to load staff data",
            severity: "error"
          });
        } finally {
          setLoading((prev) => ({ ...prev, operators: false }));
        }
      }
    };
    fetchAllStaff();
  }, [selectedMachineGroupCode, selectedMachineCode]);

  const handleSelectCodeChange = (event) => {
    const code = event.target.value;
    setSelectedMachineCode(code);
    setSelectedOperators([]);
    setSelectedHelpers([]);
    setSelectedAssistantOperators([]);
  };

  const handleSelectGroupChange = (event) => {
    const groupCode = event.target.value;
    setSelectedMachineGroupCode(groupCode);
    setSelectedMachineCode("");
    setSelectedOperators([]);
    setSelectedAssistantOperators([]);
    setSelectedHelpers([]);
    setRemarks("");
  };

  const handleSelectOperatorsChange = (event, value) => {
    setSelectedOperators(value);
  };

  const handleSelectHelpersChange = (event, value) => {
    setSelectedHelpers(value);
  };

  const handleSelectAssistantOperatorsChange = (event, value) => {
    setSelectedAssistantOperators(value);
  };

  const handleRemarksChange = (event) => {
    setRemarks(event.target.value);
  };

  const isFormValid = () => {
    return (
      selectedMachineGroupCode &&
      selectedMachineCode &&
      (selectedOperators.length > 0 ||
        selectedHelpers.length > 0 ||
        selectedAssistantOperators.length > 0)
    );
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      setSnackbar({
        open: true,
        message: "Please complete all required fields before submitting.",
        severity: "warning"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const trimmedRemarks = remarks.replace(/\s{2,}/g, " ").trim();
      await axios.post(`${API_BASE_URL}/logShift`, {
        employeeId: username,
        loggedInDateTime: new Date().toISOString(),
        machineCode: selectedMachineCode,
        operators: selectedOperators,
        assistantOperators: selectedAssistantOperators,
        helpers: selectedHelpers,
        activeStatus: "Logged-IN",
        remarks: trimmedRemarks.trim()
      });

      setMachineCode(selectedMachineCode);
      localStorage.setItem("machineCode", selectedMachineCode);

      setSnackbar({
        open: true,
        message: "Shift details submitted successfully! Redirecting...",
        severity: "success"
      });

      setTimeout(() => {
        navigate("/MainCategories", { state: { username } });
      }, 1500);
    } catch (error) {
      console.error("Error inserting data:", error);
      setSnackbar({
        open: true,
        message: "Failed to submit shift details. Please try again.",
        severity: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      window.history.pushState(null, document.title, window.location.href);
      alert(
        "You can't go back from this window-කණගාටුයි! ඔබට මෙම පේජ් එකෙන් පසුපසට යා නොහැක."
      );
    };

    window.addEventListener("popstate", handlePopState);
    window.history.pushState(null, document.title, window.location.href);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  const getTotalSelectedStaff = () => {
    return (
      selectedOperators.length +
      selectedHelpers.length +
      selectedAssistantOperators.length
    );
  };

  return (
    <div className="banner">
      <Box
        sx={{
          padding: 2,
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          bgcolor: (t) => t.palette.background.default
        }}
      >
        <Zoom in={formVisible} timeout={800}>
          <Card
            sx={{
              width: "950px",
              height: "550px",
              borderRadius: "20px",
              bgcolor: (t) => alpha(t.palette.background.paper, 0.96),
              backdropFilter: "blur(20px)",
              border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
              boxShadow: 6,
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              "&:hover": { boxShadow: 10 },
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "3px",
                background: (t) =>
                  `linear-gradient(90deg, ${t.palette.primary.main}, ${t.palette.secondary.main}, ${t.palette.primary.main})`,
                backgroundSize: "200% 100%",
                animation: "gradient 2s ease infinite"
              }
            }}
          >
            {isSubmitting && (
              <LinearProgress
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  borderRadius: "20px 20px 0 0",
                  "& .MuiLinearProgress-bar": {
                    background: (t) =>
                      `linear-gradient(90deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`
                  }
                }}
              />
            )}

            {/* Header */}
            <Box
              sx={{
                textAlign: "center",
                p: 2,
                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                backgroundColor: (t) => alpha(t.palette.primary.main, 0.04)
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2
                }}
              >
                <PrecisionManufacturingIcon
                  sx={{
                    fontSize: 36,
                    color: (t) => t.palette.primary.main,
                    filter: (t) =>
                      `drop-shadow(0 2px 4px ${alpha(
                        t.palette.primary.main,
                        0.3
                      )})`
                  }}
                />
                <Typography
                  variant="h4"
                  fontWeight="700"
                  sx={{
                    background: (t) =>
                      `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent"
                  }}
                >
                  Shift Configuration Dashboard
                </Typography>
              </Box>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontWeight: 500, mt: 0.5 }}
              >
                Configure your shift assignments and team details
              </Typography>
            </Box>

            {/* Main Content Area */}
            <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
              <Grid container spacing={3} sx={{ height: "100%" }}>
                {/* Left Panel - Machine Configuration */}
                <Grid item xs={5}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: "100%",
                      p: 2.5,
                      backgroundColor: (t) => alpha(t.palette.primary.main, 0.04),
                      borderRadius: "16px",
                      border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.1)}`
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: (t) => t.palette.primary.main,
                        fontWeight: 600
                      }}
                    >
                      <FactoryIcon />
                      Machine Configuration
                    </Typography>

                    {/* Machine Group Selection */}
                    <FormControl fullWidth sx={{ mb: 2.5 }}>
                      <InputLabel>Machine Group</InputLabel>
                      <Select
                        label="Machine Group"
                        value={selectedMachineGroupCode}
                        onChange={handleSelectGroupChange}
                        disabled={loading.groups}
                        sx={{
                          borderRadius: "12px",
                          backgroundColor: (t) => t.palette.background.paper,
                          "& .MuiOutlinedInput-root": { borderRadius: "12px" }
                        }}
                      >
                        {machineGroups.map((group) => (
                          <MenuItem key={group} value={group}>
                            {group}
                          </MenuItem>
                        ))}
                      </Select>
                      {loading.groups && (
                        <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />
                      )}
                    </FormControl>

                    {/* Machine Code Selection */}
                    <Fade in={machineNames.length > 0} timeout={500}>
                      <FormControl fullWidth sx={{ mb: 2.5 }}>
                        <InputLabel>Machine Code</InputLabel>
                        <Select
                          label="Machine Code"
                          value={selectedMachineCode}
                          onChange={handleSelectCodeChange}
                          disabled={loading.machines}
                          sx={{
                            borderRadius: "12px",
                            backgroundColor: (t) => t.palette.background.paper,
                            "& .MuiOutlinedInput-root": { borderRadius: "12px" }
                          }}
                        >
                          {machineNames.map((name) => (
                            <MenuItem key={name} value={name}>
                              {name}
                            </MenuItem>
                          ))}
                        </Select>
                        {loading.machines && (
                          <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />
                        )}
                      </FormControl>
                    </Fade>

                    {/* Selection Status */}
                    {selectedMachineGroupCode && selectedMachineCode && (
                      <Fade in timeout={500}>
                        <Box
                          sx={{
                            p: 2,
                            backgroundColor: (t) =>
                              alpha(t.palette.success.main, 0.1),
                            borderRadius: "12px",
                            border: (t) =>
                              `1px solid ${alpha(t.palette.success.main, 0.2)}`
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1
                            }}
                          >
                            <CheckCircleIcon
                              sx={{ color: "success.main", fontSize: 20 }}
                            />
                            <Typography
                              variant="subtitle2"
                              color="success.main"
                              fontWeight={600}
                            >
                              Configuration Complete
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Group:</strong> {selectedMachineGroupCode}
                            <br />
                            <strong>Machine:</strong> {selectedMachineCode}
                          </Typography>
                        </Box>
                      </Fade>
                    )}

                    {/* Remarks Section */}
                    <Fade
                      in={selectedMachineGroupCode && selectedMachineCode}
                      timeout={600}
                    >
                      <Box sx={{ mt: 3 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            mb: 1.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            fontWeight: 600,
                            color: (t) => t.palette.primary.main
                          }}
                        >
                          <CommentIcon />
                          Remarks
                        </Typography>
                        <Box sx={{ position: "relative" }}>
                          <TextField
                            multiline
                            rows={3}
                            value={remarks}
                            onChange={handleRemarksChange}
                            variant="outlined"
                            fullWidth
                            placeholder="Add any additional comments or notes..."
                            inputProps={{ maxLength: 80 }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                                backgroundColor: (t) => t.palette.background.paper
                              }
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              position: "absolute",
                              bottom: 8,
                              right: 12,
                              color: (t) =>
                                remarks.length >= 80
                                  ? t.palette.error.main
                                  : t.palette.text.secondary,
                              fontWeight: remarks.length >= 80 ? "bold" : "normal",
                              backgroundColor: (t) => t.palette.background.paper,
                              px: 0.5,
                              borderRadius: 1
                            }}
                          >
                            {remarks.length} / 80
                          </Typography>
                        </Box>
                      </Box>
                    </Fade>
                  </Paper>
                </Grid>

                {/* Right Panel - Staff Assignment */}
                <Grid item xs={7}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: "100%",
                      p: 2.5,
                      backgroundColor: (t) => alpha(t.palette.secondary.main, 0.04),
                      borderRadius: "16px",
                      border: (t) =>
                        `1px solid ${alpha(t.palette.secondary.main, 0.1)}`,
                      display: "flex",
                      flexDirection: "column"
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: (t) => t.palette.secondary.main,
                        fontWeight: 600
                      }}
                    >
                      <AssignmentIndIcon />
                      Team Assignment
                      {getTotalSelectedStaff() > 0 && (
                        <Chip
                          label={`${getTotalSelectedStaff()} selected`}
                          size="small"
                          color="secondary"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>

                    <Fade
                      in={selectedMachineGroupCode && selectedMachineCode}
                      timeout={600}
                    >
                      <Box
                        sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}
                      >
                        {operatorNames.length > 0 && (
                          <Autocomplete
                            multiple
                            filterSelectedOptions
                            options={operatorNames}
                            value={selectedOperators}
                            onChange={handleSelectOperatorsChange}
                            disabled={loading.operators}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Operators"
                                placeholder="Choose operators..."
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    backgroundColor: (t) =>
                                      t.palette.background.paper
                                  }
                                }}
                              />
                            )}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  variant="outlined"
                                  label={option}
                                  color="primary"
                                  size="small"
                                  {...getTagProps({ index })}
                                  key={index}
                                />
                              ))
                            }
                          />
                        )}

                        {assistantOperatorNames.length > 0 && (
                          <Autocomplete
                            multiple
                            filterSelectedOptions
                            options={assistantOperatorNames}
                            value={selectedAssistantOperators}
                            onChange={handleSelectAssistantOperatorsChange}
                            disabled={loading.operators}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Assistant Operators"
                                placeholder="Choose assistant operators..."
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    backgroundColor: (t) =>
                                      t.palette.background.paper
                                  }
                                }}
                              />
                            )}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  variant="outlined"
                                  label={option}
                                  color="secondary"
                                  size="small"
                                  {...getTagProps({ index })}
                                  key={index}
                                />
                              ))
                            }
                          />
                        )}

                        {helperNames.length > 0 && (
                          <Autocomplete
                            multiple
                            filterSelectedOptions
                            options={helperNames}
                            value={selectedHelpers}
                            onChange={handleSelectHelpersChange}
                            disabled={loading.operators}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Helpers"
                                placeholder="Choose helpers..."
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                    backgroundColor: (t) =>
                                      t.palette.background.paper
                                  }
                                }}
                              />
                            )}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  variant="outlined"
                                  label={option}
                                  color="info"
                                  size="small"
                                  {...getTagProps({ index })}
                                  key={index}
                                />
                              ))
                            }
                          />
                        )}

                        {loading.operators && (
                          <LinearProgress sx={{ borderRadius: 1 }} />
                        )}

                        {/* Team Summary */}
                        {getTotalSelectedStaff() > 0 && (
                          <Box
                            sx={{
                              mt: "auto",
                              p: 2,
                              backgroundColor: (t) =>
                                alpha(t.palette.primary.main, 0.1),
                              borderRadius: "12px",
                              border: (t) =>
                                `1px solid ${alpha(t.palette.primary.main, 0.2)}`
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              fontWeight={600}
                              sx={{ mb: 1 }}
                            >
                              Team Summary
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Operators:</strong> {selectedOperators.length} •{" "}
                              <strong> Assistants:</strong>{" "}
                              {selectedAssistantOperators.length} •{" "}
                              <strong> Helpers:</strong> {selectedHelpers.length}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Fade>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            {/* Footer Action Bar */}
            <Box
              sx={{
                p: 2.5,
                borderTop: (t) => `1px solid ${t.palette.divider}`,
                backgroundColor: (t) => t.palette.action.hover,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  User: <strong>{username}</strong>
                </Typography>
                {isFormValid() && (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Ready to Submit"
                    color="success"
                    size="small"
                  />
                )}
              </Box>

              <Box sx={{ display: "flex", gap: 2 }}>
                <UserLogOut username={username} />
                <Button
                  variant="contained"
                  disabled={isSubmitting || !isFormValid()}
                  onClick={handleSubmit}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} sx={{ color: "common.white" }} />
                    ) : (
                      <PublishIcon />
                    )
                  }
                  sx={{
                    minWidth: 200,
                    borderRadius: "12px",
                    py: 1.2,
                    fontWeight: 600,
                    fontSize: "1rem",
                    textTransform: "none",
                    color: "common.white",
                    backgroundImage: (t) =>
                      isFormValid()
                        ? `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`
                        : `linear-gradient(135deg, ${alpha(
                            t.palette.text.disabled,
                            0.5
                          )} 0%, ${t.palette.text.disabled} 100%)`,
                    boxShadow: (t) =>
                      isFormValid()
                        ? `0 6px 20px ${alpha(t.palette.primary.main, 0.4)}`
                        : `0 3px 10px ${alpha(t.palette.text.disabled, 0.3)}`,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover:not(:disabled)": {
                      transform: "translateY(-2px)",
                      boxShadow: (t) =>
                        `0 8px 25px ${alpha(t.palette.primary.main, 0.5)}`
                    }
                  }}
                >
                  {isSubmitting ? "Submitting..." : "Submit Shift Details"}
                </Button>
              </Box>
            </Box>
          </Card>
        </Zoom>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={handleSnackbarClose}
          sx={{ borderRadius: "8px", fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <style jsx>{`
        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
      `}</style>
    </div>
  );
};

export default ShiftDetails;
